import { loadPdfAndExtractLines } from './extractPdfText.js';
import {
    extractAtsCvFormFromImportLines,
    extractAtsCvFormFromPdfMetadata,
} from './atsCvPdfImportPayload.js';
import {
    mergeImportedAtsCvForm,
    normalizeExtractedCvLines,
    parseAtsCvFromLines,
} from './parseAtsCvFromText.js';

const MAX_PDF_BYTES = 8 * 1024 * 1024;

/**
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<{ form: import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM, warnings: string[], lineCount: number, pageCount: number, usedOcr?: boolean }>}
 */
export async function importAtsCvFromPdf(file, onProgress = () => {}) {
    if (!file) {
        throw new Error("কোনো ফাইল নির্বাচন করা হয়নি।");
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        throw new Error("শুধুমাত্র PDF ফাইল সমর্থিত।");
    }
    if (file.size > MAX_PDF_BYTES) {
        throw new Error("PDF সর্বোচ্চ ৮ MB হতে পারে।");
    }

    let lines = [];
    let pageCount = 0;
    let usedOcr = false;

    const { lines: extractedLines, pageCount: pages, pdf } = await loadPdfAndExtractLines(
        file,
        (pct) => onProgress(Math.round(pct * 0.35))
    );
    lines = extractedLines;
    pageCount = pages;

    const embeddedFromMeta = await extractAtsCvFormFromPdfMetadata(pdf);
    if (embeddedFromMeta) {
        onProgress(100);
        return {
            form: embeddedFromMeta,
            warnings: [
                "এই টুল থেকে তৈরি PDF — সব তথ্য সম্পূর্ণভাবে পুনরুদ্ধার করা হয়েছে।",
            ],
            lineCount: lines.length,
            pageCount,
            usedOcr,
        };
    }

    let normalized = normalizeExtractedCvLines(lines);

    const embeddedFromLines = extractAtsCvFormFromImportLines(normalized);
    if (embeddedFromLines) {
        onProgress(100);
        return {
            form: embeddedFromLines,
            warnings: [
                "এই টুল থেকে তৈরি PDF — সব তথ্য সম্পূর্ণভাবে পুনরুদ্ধার করা হয়েছে।",
            ],
            lineCount: lines.length,
            pageCount,
            usedOcr,
        };
    }

    if (!lines.length) {
        onProgress(40);
        const { extractPdfLinesViaOcr } = await import("./extractPdfText.js");
        const ocrResult = await extractPdfLinesViaOcr(file, (pct) =>
            onProgress(40 + Math.round(pct * 0.6))
        );
        lines = ocrResult.lines;
        pageCount = ocrResult.pageCount;
        usedOcr = true;
        normalized = normalizeExtractedCvLines(lines);
    }

    if (!lines.length) {
        throw new Error(
            "এই PDF থেকে টেক্সট পড়া যায়নি। নতুন করে এই টুল থেকে PDF ডাউনলোড করুন (টেক্সট-ভিত্তিক) অথবা অন্য টেক্সট PDF চেষ্টা করুন।"
        );
    }

    // Drop near-duplicate lines from image+text layered PDFs (keep first occurrence)
    const seen = new Set();
    normalized = normalized.filter((line) => {
        const key = line.toLowerCase().replace(/\s+/g, " ");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    const parsed = parseAtsCvFromLines(normalized);
    const warnings = parsed.warnings || [];
    delete parsed.warnings;

    if (usedOcr) {
        warnings.unshift(
            "ছবি-ভিত্তিক PDF ছিল — OCR দিয়ে পড়া হয়েছে। তথ্য যাচাই করে প্রয়োজনে ঠিক করুন।"
        );
    } else {
        warnings.unshift(
            "পুরনো বা অন্য সোর্সের PDF — যতটা সম্ভব পড়া হয়েছে। যাচাই করে প্রয়োজনীয় অংশ ঠিক করুন। নতুন PDF ডাউনলোড করলে সম্পূর্ণ ডেটা ফিরে পাবেন।"
        );
    }

    const form = mergeImportedAtsCvForm(parsed);

    if (!form.fullName && !form.summary && !form.experiences.some((e) => e.jobTitle)) {
        warnings.push(
            "সীমিত তথ্য পাওয়া গেছে। ইম্পোর্ট পর ডেটা যাচাই করে প্রয়োজনীয় ক্ষেত্র পূরণ করুন।"
        );
    }

    return {
        form,
        warnings,
        lineCount: lines.length,
        pageCount,
        usedOcr,
    };
}
